import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { getSubscriptionSettingsAPI } from "../lib/api/api";
import { useSelector } from "react-redux";

const NOTIFICATION_TARGET = "/settings";

export default function IntegrationNotifications() {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 12,
  });

  const location = useLocation();
  const authStatus = useSelector((state) => state.auth.status);
  const authUserId = useSelector(
    (state) =>
      state.auth.authData?.user_id ??
      state.auth.authData?.id ??
      state.auth.authData?.user?.id ??
      null
  );
  const isOnSettingsPage = location.pathname.startsWith("/settings");

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["header", "subscription-summary", authStatus, authUserId],
    queryFn: getSubscriptionSettingsAPI,
    staleTime: 1000 * 60 * 5,
    enabled: authStatus === "authorized" && isOpen && isOnSettingsPage,
  });

  const notifications = useMemo(() => {
    if (!data) return [];
    const list = [];

    if (!data.discord_id) {
      list.push({
        id: "discord",
        title: "Discord nicht verknüpft",
        description:
          "Profitiere von unserer Discord Community mit Live-Support, Seller-Erfahrungen und Austausch mit anderen Sellern.",
      });
    }

    // Temporarily hide the Amazon warning so only the Discord reminder shows up.
    // if (!data.seller_id) {
    //   list.push({
    //     id: "amazon",
    //     title: "Amazon Seller Central fehlt",
    //     description:
    //       "Verbinde dein Seller Central, um das volle Potenzial von Edge zu nutzen.",
    //   });
    // }

    return list;
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const clickedButton =
        containerRef.current &&
        containerRef.current.contains(target);
      const clickedDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);
      if (!clickedButton && !clickedDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && typeof window !== "undefined" && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: Math.max(window.innerWidth - rect.right, 12),
      });
    }
  }, [isOpen]);

  const badgeCount = notifications.length;
  const hasNotifications = badgeCount > 0;
  const showDropdown = isOpen;

  const notificationLabel = hasNotifications
    ? badgeCount === 1
      ? "Neue Benachrichtigung"
      : "Neue Benachrichtigungen"
    : "Keine Benachrichtigung";

  return (
    <div className="relative flex flex-col items-end" ref={containerRef}>
      {/* Icon-only Button (Tooltips erstmal raus). */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        ref={triggerRef}
        className={classNames(
          "relative inline-flex h-10 w-10 items-center justify-center rounded-md transition hover:opacity-80",
          "bg-light-mode-header text-textTitle",
          "dark:bg-dark-mode-header dark:text-white"
        )}
        aria-label={notificationLabel}
      >
        <i className="bx bx-bell text-[18px] text-primary dark:text-emerald-300"></i>
        {hasNotifications &&
          (badgeCount < 10 ? (
            <span className="absolute -top-1.5 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white dark:text-black">
              {badgeCount}
            </span>
          ) : (
            <span className="absolute -top-1.5 -right-1 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-[1px] text-[10px] font-bold text-white dark:text-black">
              <i className="bx bx-bell-plus text-xs"></i>
              {badgeCount}
            </span>
          ))}
      </button>

      {showDropdown &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] w-72 max-w-[90vw] rounded-lg border border-borderBottom bg-bgColor p-3 text-sm shadow-xl dark:bg-dark-mode-header"
            style={{
              top: dropdownPosition.top,
              right: dropdownPosition.right,
            }}
          >
            {isLoading || isFetching ? (
              <div className="flex items-center gap-2 text-xs text-textPrimary">
                <i className="bx bx-loader-alt animate-spin text-base"></i>
                Lade Benachrichtigungen …
              </div>
            ) : isError ? (
              <div className="space-y-2 text-xs text-red-500">
                <p>
                  {error?.message ||
                    "Benachrichtigungen konnten nicht geladen werden."}
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-primary underline decoration-dotted underline-offset-4"
                >
                  Erneut versuchen
                </button>
              </div>
            ) : hasNotifications ? (
              <ul className="space-y-2">
                {notifications.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-md border border-borderBottom bg-background p-3 dark:bg-dark-mode-header/60"
                  >
                    <p className="text-sm font-semibold text-textTitle dark:text-white">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-textPrimary">
                      {item.description}
                    </p>
                    <Link
                      to={NOTIFICATION_TARGET}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>Jetzt erledigen</span>
                      <i className="bx bx-link-external text-sm"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-textPrimary">
                <p className="font-semibold text-textTitle dark:text-white">
                  Keine Benachrichtigung
                </p>
                <p className="mt-1">Alles im grünen Bereich.</p>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
